import React from "react";
import {
  Document,
  Page,
  Text,
  View,
  StyleSheet,
  Font,
  Image,
} from "@react-pdf/renderer";
import dayjs from "dayjs";

// Font Registration - Stick to weights we have URLs for
Font.register({
  family: "Manrope",
  fonts: [
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-400-normal.ttf",
      fontWeight: "normal",
    },
    {
      src: "https://cdn.jsdelivr.net/fontsource/fonts/manrope@latest/latin-700-normal.ttf",
      fontWeight: "bold",
    },
  ],
});

const styles = StyleSheet.create({
  page: {
    paddingTop: 5,
    paddingBottom: 70,
    fontSize: 10,
    fontFamily: "Manrope",
    color: "#333",
  },
  headerSpace: {
    width: "100%",
    height: 80,
    marginBottom: 20,
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
  },
  header_image: {
    width: "95%",
    height: "100%",
    objectFit: "contain",
  },
  contentWrapper: {
    paddingHorizontal: 30,
  },
  mainTitle: {
    fontSize: 16,
    fontWeight: "bold",
    textAlign: "center",
    textTransform: "uppercase",
    marginBottom: 15,
    borderBottomWidth: 1.5,
    borderBottomColor: "#000",
    paddingBottom: 3,
  },
  infoSection: {
    flexDirection: "row",
    borderWidth: 1,
    borderColor: "#000",
  },
  infoBox: {
    flex: 1,
    borderRightWidth: 1,
    borderRightColor: "#000",
    padding: 6,
  },
  rightBox: {
    width: 120,
    padding: 6,
  },
  label: {
    fontSize: 7,
    color: "#666",
    textTransform: "uppercase",
    marginBottom: 2,
    fontWeight: "bold",
  },
  value: {
    fontSize: 9,
    fontWeight: "bold",
  },
  fullWidthBox: {
    width: "100%",
    borderWidth: 1,
    borderTopWidth: 0,
    borderColor: "#000",
    padding: 6,
    marginBottom: 15,
  },
  fcValue: {
    fontSize: 8,
    fontWeight: "normal",
    color: "#444",
    marginTop: 2,
  },
  tableHeader: {
    flexDirection: "row",
    backgroundColor: "#f2f2f2",
    borderWidth: 1,
    borderColor: "#000",
  },
  tableRow: {
    flexDirection: "row",
    borderLeftWidth: 1,
    borderRightWidth: 1,
    borderBottomWidth: 1,
    borderColor: "#000",
    minHeight: 22,
    alignItems: "center",
  },
  cell: { flex: 1, padding: 4, fontSize: 8 },
  cellRight: {
    flex: 0.8,
    padding: 4,
    fontSize: 8,
    textAlign: "right",
    fontWeight: "bold",
  },
  signatureSection: {
    position: "absolute",
    bottom: 60,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
  },
  signBox: {
    width: "20%",
    borderTopWidth: 1,
    borderTopColor: "#000",
    paddingTop: 5,
    textAlign: "center",
    fontSize: 8,
    fontWeight: "bold",
  },
  disclaimer: {
    position: "absolute",
    bottom: 35,
    left: 30,
    fontSize: 7,
    color: "#666",
    // REMOVED fontStyle: "italic" to prevent the font resolution error
  },
  footerPageInfo: {
    position: "absolute",
    bottom: 15,
    left: 30,
    right: 30,
    flexDirection: "row",
    justifyContent: "space-between",
    fontSize: 7,
    color: "grey",
  },
});

const RecieptDocument = ({ accountData }) => {
  const reciept = Array.isArray(accountData) ? accountData : [];
  const data = reciept[0] || {};
  const CURRENTUSEROBJECT = JSON.parse(
    localStorage.getItem("currentUser") || "[]",
  );

  let headerImage = null;
  try {
    const apiPaths = JSON.parse(localStorage.getItem("apiPaths") || "[]");
    headerImage = apiPaths[0]?.REP_HEADER;
  } catch (e) {
    console.error("Local storage error:", e);
  }

  const filteredReceiptData = reciept.filter(
    (inv) =>
      String(inv?.type).trim().toUpperCase() === "C" && inv?.pdc_acc === "X",
  );
  console.log("Filtered Receipt Data:", filteredReceiptData);
  console.log("account data", accountData);
  const creditEntry = (accountData || []).find(
    (inv) => String(inv?.type).trim().toUpperCase() === "D",
  );
  const formatCurrency = (amt) =>
    Number(amt || 0).toLocaleString(undefined, { minimumFractionDigits: 2 });
  // Determine if Foreign Currency needs to be shown based on exchange rate
  const showFC = data?.rate && Number(data.rate) !== 1;
  const basicCurrency =
    accountData?.find((item) => item?.rate === 1)?.curno ||
    CURRENTUSEROBJECT[0]?.CURRENCY;
  console.log("basicCurrency", basicCurrency);

  return (
    <Document>
      <Page size="A4" style={styles.page}>
        <View style={styles.headerSpace} fixed>
          {headerImage && (
            <Image src={headerImage} style={styles.header_image} />
          )}
        </View>

        <View style={styles.contentWrapper}>
          <Text style={styles.mainTitle}>Receipt Voucher</Text>

          {/* Top Row: Voucher Info */}
          <View style={styles.infoSection}>
            <View style={styles.infoBox}>
              <Text style={styles.label}>Voucher No</Text>
              <Text style={styles.value}>{data?.TYPE_NO?.trim()}</Text>
            </View>
            <View style={styles.infoBox}>
              <Text style={styles.label}>Date</Text>
              <Text style={styles.value}>
                {data?.JVDATE ? dayjs(data.JVDATE).format("DD-MM-YYYY") : "-"}
              </Text>
            </View>
            <View style={styles.rightBox}>
              <Text style={styles.label}>Basic Currency</Text>
              <Text style={styles.value}>{basicCurrency}</Text>
            </View>
          </View>

          {/* Middle Row: Paid To (Debit) & Total Amount */}
          <View style={[styles.infoSection, { borderTopWidth: 0 }]}>
            <View style={[styles.infoBox, { flex: 2 }]}>
              <Text style={styles.label}>
                Received From / Account Name (Cr)
              </Text>
              <Text style={styles.value}>
                {data?.CUST_ACC} - {data?.ACCDESC}
              </Text>
            </View>
            <View style={[styles.rightBox, { backgroundColor: "#fafafa" }]}>
              <Text style={styles.label}>Total Amount</Text>
              <Text style={[styles.value, { fontSize: 11 }]}>
                {formatCurrency(data?.AMOUNT)} {basicCurrency}
              </Text>
              {/* Conditionally displays Foreign Currency details right below */}
              {showFC && (
                <Text style={styles.fcValue}>
                  ({data?.curno} {formatCurrency(data?.FCAMT)} @ {data?.rate})
                </Text>
              )}
            </View>
          </View>

          {/* Bank/Cash Account Row (Credit) */}
          <View style={[styles.infoSection, { borderTopWidth: 0 }]}>
            <View style={[styles.infoBox, { flex: 1 }]}>
              <Text style={styles.label}>Received Via (Bank/Cash) (Dr)</Text>
              <Text style={styles.value}>
                {creditEntry
                  ? `${creditEntry.account} - ${creditEntry.jvnum}`
                  : "N/A"}{" "}
              </Text>
            </View>
          </View>

          {/* Conditional Cheque Details Row */}
          {data?.chqno && (
            <View style={[styles.infoSection, { borderTopWidth: 0 }]}>
              <View style={styles.infoBox}>
                <Text style={styles.label}>Cheque No</Text>
                <Text style={styles.value}>{data.chqno}</Text>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.label}>Cheque Date</Text>
                <Text style={styles.value}>
                  {data.chqdate
                    ? dayjs(data.chqdate).format("DD-MM-YYYY")
                    : "-"}
                </Text>
              </View>
              <View style={styles.infoBox}>
                <Text style={styles.label}>Bank Name</Text>
                <Text style={styles.value}>
                  {data.Expr1 ? data.Expr1 : "-"}
                </Text>
              </View>
            </View>
          )}

          <View style={styles.fullWidthBox}>
            <Text style={styles.label}>Remarks</Text>
            <Text style={styles.value}>
              {data?.COMMENTS || data?.COMMENTS || "-"}
            </Text>
          </View>

          {/* Table Header */}
          <View style={styles.tableHeader}>
            <Text style={[styles.cell, { fontWeight: "bold" }]}>Reference</Text>
            <Text style={[styles.cell, { fontWeight: "bold" }]}>Inv Date</Text>
            <Text style={[styles.cell, { flex: 1.5, fontWeight: "bold" }]}>
              Description
            </Text>
            <Text style={[styles.cellRight, { fontWeight: "bold" }]}>
              Amount
            </Text>
          </View>

          {/* Table Data */}
          {filteredReceiptData?.map((inv, idx) => (
            <View style={styles.tableRow} key={idx}>
              <Text style={styles.cell}>{inv.ref?.trim() || "-"}</Text>
              <Text style={styles.cell}>
                {inv.Expr2 ? dayjs(inv.Expr2).format("DD-MMM-YYYY") : "-"}
              </Text>
              <Text style={[styles.cell, { flex: 1.5 }]}>{inv.entry_ref}</Text>
              <Text style={styles.cellRight}>{formatCurrency(inv.VAMT)}</Text>
            </View>
          ))}
        </View>

        {/* Disclaimer - Normal style now to prevent font error */}
        <Text style={styles.disclaimer} fixed>
          * This receipt is valid subject to realization of cheques.
        </Text>

        <View style={styles.signatureSection} fixed>
          <Text style={styles.signBox}>Prepared By</Text>
          <Text style={styles.signBox}>Checked By</Text>
          <Text style={styles.signBox}>Approved By</Text>
          <Text style={styles.signBox}>Receiver's Sign</Text>
        </View>

        <View style={styles.footerPageInfo} fixed>
          <Text>{dayjs().format("DD-MM-YYYY h:mm A")} | System Generated</Text>
          <Text
            render={({ pageNumber, totalPages }) =>
              `Page ${pageNumber} of ${totalPages}`
            }
          />
        </View>
      </Page>
    </Document>
  );
};

export default RecieptDocument;
